import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle2, Loader2, Send, ShieldCheck } from 'lucide-react';

const buildInitialForm = (email = '') => ({
  contact_name: '',
  contact_email: email,
  contact_phone: '',
  company_name: '',
  website_url: '',
  linkedin_url: '',
  instagram_url: '',
  facebook_url: '',
  youtube_url: '',
  tiktok_url: '',
  other_urls: '',
  business_description: '',
  service_goal: '',
  current_tools: '',
  required_integrations: '',
  api_access_notes: '',
  assets_links: '',
  deadline: '',
  additional_notes: ''
});

export const PostPaymentIntakeForm = ({ apiBase, sessionId, paymentInfo }) => {
  const bookingType = paymentInfo?.metadata?.booking_type || '';
  const packageName = paymentInfo?.metadata?.package_name || 'Servicio Neuralia369';
  const packageId = paymentInfo?.metadata?.package_id || '';
  const defaultEmail = paymentInfo?.metadata?.email && paymentInfo?.metadata?.email !== 'no_email' ? paymentInfo.metadata.email : '';
  const [form, setForm] = useState(buildInitialForm(defaultEmail));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchExistingIntake = async () => {
      try {
        const response = await fetch(`${apiBase}/service-intake/${sessionId}`);
        const data = await response.json();
        if (data.intake) {
          setForm({ ...buildInitialForm(defaultEmail), ...data.intake });
          setSubmitted(true);
        } else {
          setForm(buildInitialForm(defaultEmail));
        }
      } catch (e) {
        setError('No se pudo cargar el briefing del servicio. Puedes completarlo manualmente aquí abajo.');
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) fetchExistingIntake();
  }, [apiBase, defaultEmail, sessionId]);

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${apiBase}/service-intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, ...form })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'No se pudo guardar el briefing');
      }
      setForm({ ...buildInitialForm(defaultEmail), ...data.intake });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'No se pudo enviar la información');
    } finally {
      setSaving(false);
    }
  };

  const bookingUrl = useMemo(() => bookingType ? `/agenda?type=${bookingType}` : null, [bookingType]);

  if (loading) {
    return (
      <div className="glass-card p-golden-lg mt-golden-lg" data-testid="service-intake-loading">
        <div className="flex items-center justify-center gap-2 text-text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Cargando briefing del servicio...
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-golden-lg mt-golden-lg text-left" data-testid="service-intake-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-golden-md">
        <div>
          <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Siguiente paso obligatorio</p>
          <h3 className="text-text-heading text-xl font-heading font-semibold">Briefing del servicio</h3>
          <p className="text-text-body text-sm mt-2 max-w-2xl">
            Completa este formulario con toda la información necesaria para que el equipo de Neuralia369 pueda empezar a trabajar en <span className="text-primary font-medium">{packageName}</span> sin pérdida de tiempo.
          </p>
        </div>
        <div className="bg-card border border-border-subtle rounded-xl px-4 py-3">
          <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Referencia</p>
          <p className="text-text-heading text-sm font-medium">{packageId}</p>
        </div>
      </div>

      {submitted && (
        <div className="mb-golden-md bg-green-500/10 border border-green-500/25 rounded-xl p-4" data-testid="service-intake-success-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-green-300 font-medium">Briefing guardado correctamente.</p>
              <p className="text-text-body text-sm mt-1">Puedes actualizarlo si necesitas añadir más URLs, integraciones o notas.</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-golden-md bg-red-500/10 border border-red-500/25 rounded-xl p-4 text-red-300 text-sm" data-testid="service-intake-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-golden-lg" data-testid="service-intake-form">
        <div className="grid md:grid-cols-2 gap-golden-lg">
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Contacto principal *</label>
            <input className="input-kinesthetic" value={form.contact_name} onChange={(e) => handleChange('contact_name', e.target.value)} required data-testid="intake-contact-name" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Email de contacto *</label>
            <input type="email" className="input-kinesthetic" value={form.contact_email} onChange={(e) => handleChange('contact_email', e.target.value)} required data-testid="intake-contact-email" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Teléfono / WhatsApp</label>
            <input className="input-kinesthetic" value={form.contact_phone} onChange={(e) => handleChange('contact_phone', e.target.value)} data-testid="intake-contact-phone" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Empresa / proyecto *</label>
            <input className="input-kinesthetic" value={form.company_name} onChange={(e) => handleChange('company_name', e.target.value)} required data-testid="intake-company-name" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-golden-lg">
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Web principal</label>
            <input className="input-kinesthetic" value={form.website_url} onChange={(e) => handleChange('website_url', e.target.value)} placeholder="https://..." data-testid="intake-website-url" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">LinkedIn</label>
            <input className="input-kinesthetic" value={form.linkedin_url} onChange={(e) => handleChange('linkedin_url', e.target.value)} placeholder="https://..." data-testid="intake-linkedin-url" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Instagram</label>
            <input className="input-kinesthetic" value={form.instagram_url} onChange={(e) => handleChange('instagram_url', e.target.value)} placeholder="https://..." data-testid="intake-instagram-url" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Facebook / Meta</label>
            <input className="input-kinesthetic" value={form.facebook_url} onChange={(e) => handleChange('facebook_url', e.target.value)} placeholder="https://..." data-testid="intake-facebook-url" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">YouTube</label>
            <input className="input-kinesthetic" value={form.youtube_url} onChange={(e) => handleChange('youtube_url', e.target.value)} placeholder="https://..." data-testid="intake-youtube-url" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">TikTok</label>
            <input className="input-kinesthetic" value={form.tiktok_url} onChange={(e) => handleChange('tiktok_url', e.target.value)} placeholder="https://..." data-testid="intake-tiktok-url" />
          </div>
        </div>

        <div>
          <label className="block text-text-body mb-golden-sm text-sm">Otros URLs importantes</label>
          <textarea rows={3} className="input-kinesthetic resize-none" value={form.other_urls} onChange={(e) => handleChange('other_urls', e.target.value)} placeholder="Webs secundarias, drive, acceso a paneles, landings, funnels, documentación, etc." data-testid="intake-other-urls" />
        </div>

        <div className="grid md:grid-cols-2 gap-golden-lg">
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Describe tu negocio *</label>
            <textarea rows={4} className="input-kinesthetic resize-none" value={form.business_description} onChange={(e) => handleChange('business_description', e.target.value)} required data-testid="intake-business-description" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Objetivo del servicio *</label>
            <textarea rows={4} className="input-kinesthetic resize-none" value={form.service_goal} onChange={(e) => handleChange('service_goal', e.target.value)} required data-testid="intake-service-goal" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-golden-lg">
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Herramientas actuales</label>
            <textarea rows={4} className="input-kinesthetic resize-none" value={form.current_tools} onChange={(e) => handleChange('current_tools', e.target.value)} placeholder="CRM, Meta, GA4, Zapier, Mailchimp, WhatsApp, etc." data-testid="intake-current-tools" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Integraciones / APIs necesarias</label>
            <textarea rows={4} className="input-kinesthetic resize-none" value={form.required_integrations} onChange={(e) => handleChange('required_integrations', e.target.value)} placeholder="Stripe, Meta, Google, WhatsApp, Resend, CRM, etc." data-testid="intake-required-integrations" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-golden-lg">
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Notas sobre accesos / APIs</label>
            <textarea rows={4} className="input-kinesthetic resize-none" value={form.api_access_notes} onChange={(e) => handleChange('api_access_notes', e.target.value)} placeholder="Qué accesos ya existen, qué falta por crear, qué credenciales están pendientes, etc." data-testid="intake-api-access-notes" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Enlaces a assets / documentación</label>
            <textarea rows={4} className="input-kinesthetic resize-none" value={form.assets_links} onChange={(e) => handleChange('assets_links', e.target.value)} placeholder="Drive, brandbook, logos, docs, copy, creatividades, etc." data-testid="intake-assets-links" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-golden-lg">
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Plazo deseado</label>
            <input className="input-kinesthetic" value={form.deadline} onChange={(e) => handleChange('deadline', e.target.value)} placeholder="Ej: esta semana / antes del 15 de abril" data-testid="intake-deadline" />
          </div>
          <div>
            <label className="block text-text-body mb-golden-sm text-sm">Notas adicionales</label>
            <input className="input-kinesthetic" value={form.additional_notes} onChange={(e) => handleChange('additional_notes', e.target.value)} placeholder="Algo importante que debamos saber" data-testid="intake-additional-notes" />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="inline-flex items-center gap-2 text-xs text-text-muted">
            <ShieldCheck className="w-4 h-4 text-primary" /> Usa este briefing para centralizar toda la información del proyecto.
          </div>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="service-intake-submit">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : <><Send className="w-4 h-4" /> {submitted ? 'Actualizar briefing' : 'Guardar briefing'}</>}
          </button>
        </div>
      </form>

      {submitted && bookingUrl && (
        <div className="mt-golden-lg bg-primary/10 border border-primary/20 rounded-xl p-4" data-testid="service-intake-booking-cta-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-text-heading font-medium">Ya puedes reservar tu videollamada con consultor humano</p>
              <p className="text-text-body text-sm mt-1">Tu briefing ya está guardado. Elige fecha y hora para la reunión inicial de análisis y planificación; asignaremos automáticamente al consultor disponible.</p>
            </div>
            <Link to={bookingUrl} className="btn-primary" data-testid="service-intake-booking-cta">
              <Calendar className="w-4 h-4" /> Agendar reunión
            </Link>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PostPaymentIntakeForm;